import { HttpClient } from '@adapters/HttpClient';
import { CountriesApiAdapter, CountryOption } from '@shared/adapters/CountriesApiAdapter';

import { CourierOption, CouriersApiAdapter } from './CouriersApiAdapter';
import { PaymentMethodOption, PaymentMethodsApiAdapter } from './PaymentMethodsApiAdapter';
import { ProductOption, ProductsApiAdapter } from './ProductsApiAdapter';
import { GetShopsOptions, ShopOption, ShopsApiAdapter } from './ShopsApiAdapter';
import { StatusApiAdapter, StatusOption } from './StatusApiAdapter';

export interface PreparationOption {
  id: string;
  name: string; // Será el código de la preparación
}

export interface GeneralErrorOption {
  id: string;
  name: string;
  error_key?: string;
}

export interface FilterOptions {
  statuses: StatusOption[];
  shops: ShopOption[];
  paymentMethods: PaymentMethodOption[];
  products: ProductOption[];
  couriers: CourierOption[];
  countries: CountryOption[];
  preparations: PreparationOption[];
}

export class FiltersApiAdapter {
  private statusAdapter: StatusApiAdapter;
  private shopsAdapter: ShopsApiAdapter;
  private paymentMethodsAdapter: PaymentMethodsApiAdapter;
  private productsAdapter: ProductsApiAdapter;
  private couriersAdapter: CouriersApiAdapter;
  private countriesAdapter: CountriesApiAdapter;

  constructor(private httpClient: HttpClient) {
    this.statusAdapter = new StatusApiAdapter(httpClient);
    this.shopsAdapter = new ShopsApiAdapter(httpClient);
    this.paymentMethodsAdapter = new PaymentMethodsApiAdapter(httpClient);
    this.productsAdapter = new ProductsApiAdapter(httpClient);
    this.couriersAdapter = new CouriersApiAdapter(httpClient);
    this.countriesAdapter = new CountriesApiAdapter(httpClient);
  }

  async getAllFilterOptions(): Promise<FilterOptions> {
    try {
      const [statuses, shops, paymentMethods, products, couriers] = await Promise.all([
        this.statusAdapter.getAll(),
        this.shopsAdapter.getAll(),
        this.paymentMethodsAdapter.getAll(),
        this.productsAdapter.getAll(),
        this.couriersAdapter.getAll(),
      ]);

      return {
        statuses,
        shops,
        paymentMethods,
        products,
        couriers,
      };
    } catch (error) {
      console.error('Error loading filter options:', error);
      return {
        statuses: [],
        shops: [],
        paymentMethods: [],
        products: [],
        couriers: [],
      };
    }
  }

  async getStatuses(): Promise<StatusOption[]> {
    return this.statusAdapter.getAll();
  }

  async getShops(options?: GetShopsOptions): Promise<ShopOption[]> {
    return this.shopsAdapter.getAll(options);
  }

  async getShopsPaginated(params: {
    search?: string;
    page?: number;
    limit?: number;
    includeTeam?: boolean;
  }): Promise<{ shops: ShopOption[]; hasMore: boolean }> {
    return this.shopsAdapter.getShopsPaginated(params);
  }

  async getPaymentMethods(): Promise<PaymentMethodOption[]> {
    return this.paymentMethodsAdapter.getAll();
  }

  async getProducts(): Promise<ProductOption[]> {
    return this.productsAdapter.getAll();
  }

  async getCouriers(): Promise<CourierOption[]> {
    return this.couriersAdapter.getAll();
  }

  async getCountries(): Promise<CountryOption[]> {
    return this.countriesAdapter.getAll();
  }

  async getPreparations(): Promise<PreparationOption[]> {
    try {
      const response = await this.httpClient.get<any>('/preparations', {
        'sorting[column]': 'id',
        'sorting[direction]': 'desc',
      });
      const list = Array.isArray(response) ? response : (response.data ?? []);
      return list.map((prep: any) => ({
        id: String(prep.id),
        name: prep.code || `PREP-${prep.id}`,
      }));
    } catch (error) {
      console.error('Error loading preparations:', error);
      return [];
    }
  }

  async getGeneralErrors(): Promise<GeneralErrorOption[]> {
    try {
      const response = await this.httpClient.get<any>('/general-errors');
      const list = Array.isArray(response) ? response : (response.data ?? []);
      return list.map((error: any) => ({
        id: String(error.id),
        name: error.name || error.description || `Error ${error.id}`,
        error_key: error.error_key,
      }));
    } catch (error) {
      console.error('Error loading general errors:', error);
      return [];
    }
  }

  async getPreparationsPaginated(params: {
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<{ preparations: PreparationOption[]; hasMore: boolean }> {
    try {
      const { search = '', page = 1, limit = 20 } = params;
      const queryParams: Record<string, any> = {
        'sorting[column]': 'id',
        'sorting[direction]': 'desc',
        page,
        per_page: limit,
      };

      // Búsqueda por código
      if (search) {
        queryParams.search = search;
      }

      const response = await this.httpClient.get<any>('/preparations', queryParams);
      const list = Array.isArray(response) ? response : (response.data ?? []);
      const meta = response.meta;

      const preparations = list.map((prep: any) => ({
        id: String(prep.id),
        name: prep.code || `PREP-${prep.id}`,
      }));

      const hasMore = meta ? meta.currentPage < meta.lastPage : false;

      return { preparations, hasMore };
    } catch (error) {
      console.error('Error loading preparations:', error);
      return { preparations: [], hasMore: false };
    }
  }
}
